import { io, Socket } from 'socket.io-client';
import type { Task, Project } from '../types';

let wsErrorCount = 0;
let wsErrorToastShown = false;
const WS_ERROR_THRESHOLD = 3; // Show toast after 3 consecutive errors

function notifyWsError(context: string, err: unknown) {
  console.error(`[WebSocket] ${context}:`, err);
  wsErrorCount++;
  if (wsErrorCount >= WS_ERROR_THRESHOLD && !wsErrorToastShown) {
    wsErrorToastShown = true;
    // Dynamically import to avoid circular deps
    import('../components/common/Toast').then(({ showToast }) => {
      showToast('Connection issues detected. Some real-time updates may be delayed.', 'error');
    });
    // Reset after 30 seconds to allow re-notification
    setTimeout(() => {
      wsErrorToastShown = false;
      wsErrorCount = 0;
    }, 30000);
  }
}

/**
 * WebSocket client for real-time notification delivery and CRUD event streaming.
 *
 * Connects to the backend socket.io server using the same JWT token
 * as the REST API. Provides:
 * - Automatic reconnection with exponential backoff
 * - Event subscription for badge updates, new notifications
 * - Real-time CRUD events for tasks, projects, and reminders
 * - Graceful fallback: if WS is unavailable, polling takes over
 *
 * Usage:
 *   websocketService.connect(token);
 *   websocketService.onBadgeUpdate((data) => { ... });
 *   websocketService.onNewNotification((data) => { ... });
 *   websocketService.onTaskCreated((data) => { ... });
 *   websocketService.onProjectUpdated((data) => { ... });
 *   // etc.
 */

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Derive WS URL from API URL (strip /api suffix for socket.io path)
const getWebSocketUrl = (): string => {
  const baseUrl = API_URL.replace(/\/api$/,'').replace(/\/api\/$/, '');
  // In development, use the API URL without the /api prefix
  // socket.io will connect to the same host:port
  if (baseUrl.startsWith('http')) {
    return baseUrl;
  }
  // Relative path — use current origin
  return window.location.origin;
};

type BadgeUpdateHandler = (data: { badges: Record<string, number>; unreadCount: number }) => void;
type NewNotificationHandler = (data: Record<string, unknown>) => void;
type OrgBadgeUpdateHandler = (data: { orgCounts: Record<string, number> }) => void;

// Real-time CRUD event handler types
type TaskEventHandler = (data: { task: Task }) => void;
type TaskUpdatedHandler = (data: { task: Task; changedFields: string[] }) => void;
type TaskDeletedHandler = (data: { taskId: string }) => void;
type ProjectEventHandler = (data: { project: Project }) => void;
type ProjectUpdatedHandler = (data: { project: Project; changedFields: string[] }) => void;
type ProjectDeletedHandler = (data: { projectId: string }) => void;
// REV-05: Reminder events now include changedFields for updated events
// (consistent with task:updated and project:updated event shapes)
type ReminderEventHandler = (data: { reminder: { id: string; title: string; [key: string]: unknown }; changedFields?: string[] }) => void;
type ReminderDeletedHandler = (data: { reminderId: string }) => void;

// REV-2.4: Named interfaces for reminder event data (replaces inline types)
export interface ReminderEventData {
  reminder: { id: string; title: string; [key: string]: unknown };
}
export interface ReminderDeletedEventData {
  reminderId: string;
}

class WebSocketService {
  private socket: Socket | null = null;
  private badgeUpdateHandlers: Set<BadgeUpdateHandler> = new Set();
  private newNotificationHandlers: Set<NewNotificationHandler> = new Set();
  private orgBadgeUpdateHandlers: Set<OrgBadgeUpdateHandler> = new Set();
  // Real-time CRUD handler sets
  private taskCreatedHandlers: Set<TaskEventHandler> = new Set();
  private taskUpdatedHandlers: Set<TaskUpdatedHandler> = new Set();
  private taskDeletedHandlers: Set<TaskDeletedHandler> = new Set();
  private taskCompletedHandlers: Set<TaskEventHandler> = new Set();
  private projectCreatedHandlers: Set<ProjectEventHandler> = new Set();
  private projectUpdatedHandlers: Set<ProjectUpdatedHandler> = new Set();
  private projectDeletedHandlers: Set<ProjectDeletedHandler> = new Set();
  private projectArchivedHandlers: Set<ProjectEventHandler> = new Set();
  // REV-2.5: Reminder real-time handler sets (reminderStore now exists)
  private reminderCreatedHandlers: Set<ReminderEventHandler> = new Set();
  private reminderUpdatedHandlers: Set<ReminderEventHandler> = new Set();
  private reminderDeletedHandlers: Set<ReminderDeletedHandler> = new Set();
  private reminderCompletedHandlers: Set<ReminderEventHandler> = new Set();
  private reconnectHandlers: Set<() => void> = new Set();
  private authErrorHandlers: Set<() => void> = new Set();
  private isConnecting = false;
  private hasConnected = false;
  private currentToken: string | null = null;
  // REV-07: Connection timeout reference for safety cleanup
  private connectTimeoutRef: ReturnType<typeof setTimeout> | null = null;
  // REV-2.3: Store current org ID to re-emit switch:org on reconnect
  private currentOrgId: string | null = null;

  /**
   * Connect to the WebSocket server with the given auth token.
   * Safe to call multiple times — will reconnect if token changes.
   * REV-F2: Tracks currentToken and reconnects when token changes (e.g. after refresh).
   */
  connect(token: string): void {
    // REV-F2: If already connected with same token, nothing to do
    if (this.socket?.connected && this.hasConnected && this.currentToken === token) {
      return;
    }

    // REV-F2: If token changed, disconnect old socket before reconnecting
    if (this.socket?.connected && this.currentToken !== token) {
      this.socket.disconnect();
      this.socket = null;
      // Don't reset hasConnected — we want reconnect detection to work (REV-F3)
    }

    this.currentToken = token;

    // REV-07 fix: If isConnecting is true, force-disconnect the stale attempt
    // before starting a new connection. Previously, a stale isConnecting flag
    // would block all subsequent connect() calls, causing a permanent
    // "never connecting" state.
    if (this.isConnecting) {
      // Cancel the in-flight connection attempt
      this.disconnect();
    }

    // Disconnect existing socket if any
    this.disconnect();

    // Set isConnecting AFTER disconnect() because disconnect() resets it to false
    this.isConnecting = true;

    const wsUrl = getWebSocketUrl();

    // REV-07: Safety timeout — if connection hangs, reset isConnecting so
    // future connect() calls aren't blocked. Socket.IO has its own timeout
    // but isConnecting could remain stuck if the connect event never fires.
    if (this.connectTimeoutRef) {
      clearTimeout(this.connectTimeoutRef);
    }
    this.connectTimeoutRef = setTimeout(() => {
      if (this.isConnecting) {
        if (import.meta.env?.DEV) {
          console.warn('[WebSocket] Connection timeout — resetting isConnecting flag');
        }
        this.isConnecting = false;
      }
    }, 15000); // 15s — longer than Socket.IO's 10s timeout

    this.socket = io(wsUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 20,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      timeout: 10000,
    });

    this.socket.on('connect', () => {
      // REV-07: Clear the safety timeout since connection succeeded
      if (this.connectTimeoutRef) {
        clearTimeout(this.connectTimeoutRef);
        this.connectTimeoutRef = null;
      }
      const wasReconnect = this.hasConnected; // REV-2: detect reconnection (previously connected)
      this.hasConnected = true; // REV-3.1: Mark that we've connected at least once
      this.isConnecting = false;
      wsErrorCount = 0; // Reset error count on successful connection
      // REV-2: Fire reconnect handlers to trigger refetch of stale data
      if (wasReconnect) {
        this.reconnectHandlers.forEach(handler => {
          try {
            handler();
          } catch (err) {
            notifyWsError('Error in reconnect handler', err);
          }
        });
      // REV-2.3: Re-emit switch:org to rejoin org room after reconnect
      if (this.currentOrgId) {
        this.socket?.emit('switch:org', this.currentOrgId);
      }
      }
    });

    this.socket.on('badge:update', (data: { badges: Record<string, number>; unreadCount: number }) => {
      this.badgeUpdateHandlers.forEach(handler => {
        try {
          handler(data);
        } catch (err) {
          notifyWsError('Error in badge:update handler', err);
        }
      });
    });

    this.socket.on('notification:new', (data: Record<string, unknown>) => {
      this.newNotificationHandlers.forEach(handler => {
        try {
          handler(data);
        } catch (err) {
          notifyWsError('Error in notification:new handler', err);
        }
      });
    });

    // Per-org unread count updates for org selector dots
    this.socket.on('org-badge:update', (data: { orgCounts: Record<string, number> }) => {
      this.orgBadgeUpdateHandlers.forEach(handler => {
        try {
          handler(data);
        } catch (err) {
          notifyWsError('Error in org-badge:update handler', err);
        }
      });
    });

    // Real-time CRUD event listeners — Tasks
    this.socket.on('task:created', (data: { task: Task }) => {
      this.taskCreatedHandlers.forEach(handler => {
        try { handler(data); } catch (err) { notifyWsError('Error in task:created handler', err); }
      });
    });

    this.socket.on('task:updated', (data: { task: Task; changedFields: string[] }) => {
      this.taskUpdatedHandlers.forEach(handler => {
        try { handler(data); } catch (err) { notifyWsError('Error in task:updated handler', err); }
      });
    });

    this.socket.on('task:deleted', (data: { taskId: string }) => {
      this.taskDeletedHandlers.forEach(handler => {
        try { handler(data); } catch (err) { notifyWsError('Error in task:deleted handler', err); }
      });
    });

    this.socket.on('task:completed', (data: { task: Task }) => {
      this.taskCompletedHandlers.forEach(handler => {
        try { handler(data); } catch (err) { notifyWsError('Error in task:completed handler', err); }
      });
    });

    // Real-time CRUD event listeners — Projects
    this.socket.on('project:created', (data: { project: Project }) => {
      this.projectCreatedHandlers.forEach(handler => {
        try { handler(data); } catch (err) { notifyWsError('Error in project:created handler', err); }
      });
    });

    this.socket.on('project:updated', (data: { project: Project; changedFields: string[] }) => {
      this.projectUpdatedHandlers.forEach(handler => {
        try { handler(data); } catch (err) { notifyWsError('Error in project:updated handler', err); }
      });
    });

    this.socket.on('project:deleted', (data: { projectId: string }) => {
      this.projectDeletedHandlers.forEach(handler => {
        try { handler(data); } catch (err) { notifyWsError('Error in project:deleted handler', err); }
      });
    });

    this.socket.on('project:archived', (data: { project: Project }) => {
      this.projectArchivedHandlers.forEach(handler => {
        try { handler(data); } catch (err) { notifyWsError('Error in project:archived handler', err); }
      });
    });

    // Real-time CRUD event listeners — Reminders
    // REV-2.5: Reminder events are handled by reminderStore (dedicated store now exists)
    // by the WebSocketService so stores or components can subscribe in the future.
    this.socket.on('reminder:created', (data: ReminderEventData) => {
      this.reminderCreatedHandlers.forEach(handler => {
        try { handler(data); } catch (err) { notifyWsError('Error in reminder:created handler', err); }
      });
    });

    this.socket.on('reminder:updated', (data: ReminderEventData) => {
      this.reminderUpdatedHandlers.forEach(handler => {
        try { handler(data); } catch (err) { notifyWsError('Error in reminder:updated handler', err); }
      });
    });

    this.socket.on('reminder:deleted', (data: ReminderDeletedEventData) => {
      this.reminderDeletedHandlers.forEach(handler => {
        try { handler(data); } catch (err) { notifyWsError('Error in reminder:deleted handler', err); }
      });
    });

    this.socket.on('reminder:completed', (data: ReminderEventData) => {
      this.reminderCompletedHandlers.forEach(handler => {
        try { handler(data); } catch (err) { notifyWsError('Error in reminder:completed handler', err); }
      });
    });

    this.socket.on('disconnect', (_reason) => {
      this.isConnecting = false;
    });

    this.socket.on('connect_error', (error) => {
      // REV-07: Clear the safety timeout since connection failed
      if (this.connectTimeoutRef) {
        clearTimeout(this.connectTimeoutRef);
        this.connectTimeoutRef = null;
      }
      this.isConnecting = false;
      // In development, log connection errors for debugging
      if (import.meta.env?.DEV) {
        console.warn('[WebSocket] Connection error:', error.message);
      }
      // On auth failure, stop Socket.IO auto-reconnect and trigger auth error handlers
      // REV-F1+REV-F2: Auth handlers can attempt token refresh and reconnect
      if (error.message === 'Authentication required' ||
          error.message === 'Token expired' ||
          error.message === 'Token has been revoked' ||
          error.message === 'Invalid token') {
        if (import.meta.env?.DEV) {
          console.warn('[WebSocket] Auth error, stopping auto-reconnect and triggering auth handlers');
        }
        this.socket?.disconnect();
        // Notify auth error handlers so they can attempt token refresh + reconnect
        this.authErrorHandlers.forEach(handler => {
          try { handler(); } catch (err) { notifyWsError('Error in auth error handler', err); }
        });
      }
    });
  }

  /**
   * Disconnect from the WebSocket server.
   * REV-F3: Does NOT reset hasConnected so reconnect detection still works
   * after Socket.IO auto-reconnects. Use reset() for full cleanup on logout.
   * REV-07: Also clears the connection timeout to prevent stale timers.
   */
  disconnect(): void {
    // REV-07: Clear connection timeout to prevent stale timer callbacks
    if (this.connectTimeoutRef) {
      clearTimeout(this.connectTimeoutRef);
      this.connectTimeoutRef = null;
    }
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnecting = false;
    }
  }

  /**
   * Full reset for logout — clears token and all state.
   * Use this instead of disconnect() when the user is logging out.
   */
  reset(): void {
    this.disconnect();
    this.hasConnected = false;
    this.currentToken = null;
    this.currentOrgId = null; // REV-3.2: Clear stale org ID on logout
  }

  /**
   * Whether the WebSocket is currently connected.
   */
  get connected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Register a handler for badge updates.
   * Returns an unsubscribe function.
   */
  onBadgeUpdate(handler: BadgeUpdateHandler): () => void {
    this.badgeUpdateHandlers.add(handler);
    return () => {
      this.badgeUpdateHandlers.delete(handler);
    };
  }

  /**
   * Register a handler for new notification events.
   * Returns an unsubscribe function.
   */
  onNewNotification(handler: NewNotificationHandler): () => void {
    this.newNotificationHandlers.add(handler);
    return () => {
      this.newNotificationHandlers.delete(handler);
    };
  }

  /**
   * Register a handler for per-org unread count updates (org selector dots).
   * Returns an unsubscribe function.
   */
  onOrgBadgeUpdate(handler: OrgBadgeUpdateHandler): () => void {
    this.orgBadgeUpdateHandlers.add(handler);
    return () => {
      this.orgBadgeUpdateHandlers.delete(handler);
    };
  }

  // --- Real-time CRUD event registration methods ---
  // Each returns an unsubscribe function

  onTaskCreated(handler: TaskEventHandler): () => void {
    this.taskCreatedHandlers.add(handler);
    return () => { this.taskCreatedHandlers.delete(handler); };
  }

  onTaskUpdated(handler: TaskUpdatedHandler): () => void {
    this.taskUpdatedHandlers.add(handler);
    return () => { this.taskUpdatedHandlers.delete(handler); };
  }

  onTaskDeleted(handler: TaskDeletedHandler): () => void {
    this.taskDeletedHandlers.add(handler);
    return () => { this.taskDeletedHandlers.delete(handler); };
  }

  onTaskCompleted(handler: TaskEventHandler): () => void {
    this.taskCompletedHandlers.add(handler);
    return () => { this.taskCompletedHandlers.delete(handler); };
  }

  onProjectCreated(handler: ProjectEventHandler): () => void {
    this.projectCreatedHandlers.add(handler);
    return () => { this.projectCreatedHandlers.delete(handler); };
  }

  onProjectUpdated(handler: ProjectUpdatedHandler): () => void {
    this.projectUpdatedHandlers.add(handler);
    return () => { this.projectUpdatedHandlers.delete(handler); };
  }

  onProjectDeleted(handler: ProjectDeletedHandler): () => void {
    this.projectDeletedHandlers.add(handler);
    return () => { this.projectDeletedHandlers.delete(handler); };
  }

  onProjectArchived(handler: ProjectEventHandler): () => void {
    this.projectArchivedHandlers.add(handler);
    return () => { this.projectArchivedHandlers.delete(handler); };
  }

  // Reminder event registration methods
  // REV-2.5: Dedicated reminderStore now exists. Components subscribe via onReminder* methods.
  onReminderCreated(handler: ReminderEventHandler): () => void {
    this.reminderCreatedHandlers.add(handler);
    return () => { this.reminderCreatedHandlers.delete(handler); };
  }

  onReminderUpdated(handler: ReminderEventHandler): () => void {
    this.reminderUpdatedHandlers.add(handler);
    return () => { this.reminderUpdatedHandlers.delete(handler); };
  }

  onReminderDeleted(handler: ReminderDeletedHandler): () => void {
    this.reminderDeletedHandlers.add(handler);
    return () => { this.reminderDeletedHandlers.delete(handler); };
  }

  onReminderCompleted(handler: ReminderEventHandler): () => void {
    this.reminderCompletedHandlers.add(handler);
    return () => { this.reminderCompletedHandlers.delete(handler); };
  }

  /**
   * Register a handler that fires when the WebSocket reconnects after a disconnect.
   * Use this to trigger refetch of data that may have been missed during the outage.
   * Returns an unsubscribe function.
   */
  onReconnect(handler: () => void): () => void {
    this.reconnectHandlers.add(handler);
    return () => { this.reconnectHandlers.delete(handler); };
  }

  /**
   * Register a handler that fires when WebSocket authentication fails
   * (e.g. token expired, revoked). Use this to attempt token refresh + reconnect.
   * Returns an unsubscribe function.
   */
  onAuthError(handler: () => void): () => void {
    this.authErrorHandlers.add(handler);
    return () => { this.authErrorHandlers.delete(handler); };
  }

  /**
   * Switch the active organization context.
   * This leaves the old org room and joins the new one.
   */
  switchOrg(orgId: string): void {
    this.currentOrgId = orgId; // REV-2.3: Persist for reconnect
    if (this.socket?.connected) {
      this.socket.emit('switch:org', orgId);
    }
  }
}

/**
 * Singleton instance of the WebSocket service.
 * Import this and call connect(token) after authentication.
 */
export const websocketService = new WebSocketService();

export default websocketService;
